import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { setSEOSiteName, updateSEOUpdatedTime } from '@utils/seo';
import { useData } from '@hooks/useData';
import i18n from 'src/i18n/config';
import { useGetAllSettings } from '@services/useGetAllSettings';
import { useGetAllCarouselData } from '@services/useGetAllCarouselData';
import { useGetCart } from '@services/useGetCart';
import { GlobalStylesUpdater } from '@hooks/useGlobalStylesUpdater';
import { loadFromLocalStorage, saveToLocalStorage } from '@utils/storage';
import { useRegisterUserLocations } from '@services/useRegisterUserLocations';
import { getSessionId } from '@utils/session';
import L from 'leaflet';
import { usePushNotification } from '@hooks/usePushNotification';
import { useTranslation } from 'react-i18next';
import { useGetStoresLocations } from '@services/useGetStoresLocations';
import { getClosestStore } from '@utils/location';
import { SITE_URLS } from '@constants';
import { UserLocation } from '@types';
import { getElapsedTime, minutesToMilliseconds } from '@utils/time';
import logger from '@utils/logs';
import { CookieConsentManager } from '@components/molecules/CookieConsentManager/CookieConsentManager';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import { NoInternetPage } from '@pages/errorPages';
import { useUserLocation } from '@hooks/useUserLocation';
import { SplashScreen } from '@components/atoms/SplashScreen';

export const SiteSetup = () => {
    const isOnline = useOnlineStatus();
    const [defaultLanguage, setDefaultLanguage] = useState('');
    const { allLanguages, allSettings, isEndpointExpired, allCarouselData } = useData();
    useUserLocation();
    const { t } = useTranslation();

    const location = useLocation();
    const { language: pathLanguage } = useParams();
    const { changeLanguage } = useCustomNavigate();
    const { request: requestCart } = useGetCart();
    const { request: requestAllSettings, error: errorAllSettings, lastResponseTime: lastSettings } = useGetAllSettings();
    const { request: requestAllCarouselData } = useGetAllCarouselData();

    // prefetch carousel data in parallel with allSettings to reduce LCP resource load delay (improve performance)
    useEffect(() => {
        if (!allCarouselData) requestAllCarouselData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const currentPath = location.pathname;

    const translations = {
        skipToContent: t('site-setup-component.skip-to-content'),
    };

    // Update supported languages and default language
    useEffect(() => {
        if (allLanguages?.data) {
            const newSupportedLngs = allLanguages.data
                .map((item) => item.language_code)
                .filter((code): code is string => code !== undefined);

            const combinedLanguages = new Set<string>([...((i18n.options.supportedLngs as string[]) || []), ...newSupportedLngs]);

            // Find the default language where the default field is 1
            const defaultLng = allLanguages.data.find((item) => item.default === 1)?.language_code || i18n.language;

            i18n.languages = Array.from(combinedLanguages);
            i18n.options.fallbackLng = defaultLng;
            setDefaultLanguage(defaultLng);
        }
    }, [allLanguages]);

    const detectLanguage = useCallback(() => {
        // determines if it should change language
        const shouldChangeLanguage = pathLanguage && defaultLanguage;
        const validLanguages = allLanguages?.data.map((item) => item.language_code) || [];

        // if the language was sent in the url, and matches other available languages
        // set that language for translations
        if (shouldChangeLanguage && validLanguages.includes(pathLanguage)) {
            // use pathLanguage if valid
            changeLanguage(pathLanguage);
        } else {
            // if the language was not sent in the url,
            // try previously saved language; fallback to default
            const stored = loadFromLocalStorage<string>('language');
            if (stored && validLanguages.includes(stored)) {
                changeLanguage(stored);
            } else {
                // redirect to default translations
                changeLanguage(defaultLanguage);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changeLanguage, allLanguages]);

    useEffect(() => {
        if (defaultLanguage) {
            detectLanguage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultLanguage]);

    useEffect(() => {
        setSEOSiteName(allSettings?.data?.marketplaceName || '');

        requestCart({});

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    // Preload content images from settings so they are cached by the browser
    // even if the user goes offline before navigating to the respective pages
    useEffect(() => {
        if (!allSettings?.data) return;

        const data = allSettings.data;
        const imageKeys = Object.keys(data).filter((key) => key.startsWith('content_') && key.endsWith('_image'));

        imageKeys.forEach((key) => {
            const url = data[key as keyof typeof data];
            if (typeof url === 'string' && url) {
                const img = new Image();
                img.src = url;
            }
        });
    }, [allSettings?.data]);

    // when the URL changes
    useEffect(() => {
        // show page from the top
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

        // update og:updated_time meta tag
        updateSEOUpdatedTime();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPath]);

    // call services if we don't have the data yet,
    // or re-fetch when TTL expires (on navigation, to retry if offline)
    const isAllSettingsExpired = isEndpointExpired('allSettings');
    useEffect(() => {
        if (!allSettings || isAllSettingsExpired) {
            requestAllSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSettings, isAllSettingsExpired, currentPath]);

    // refetch settings when language changes
    const prevLanguage = useRef(i18n.language);
    useEffect(() => {
        if (prevLanguage.current !== i18n.language) {
            prevLanguage.current = i18n.language;
            requestAllSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    const handleSkipToContent = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        const mainContent = document.getElementById('main-content');
        const targetElement =
            (mainContent &&
                (Array.from(mainContent.children).find((child) => {
                    const element = child as HTMLElement;
                    const testId = element.getAttribute('data-testid')?.toLowerCase() ?? '';

                    return (
                        element.tagName === 'DIV' &&
                        Boolean(testId) &&
                        !['sticky-header', 'breadcrumbs', 'container'].includes(testId)
                    );
                }) as HTMLElement | undefined)) ||
            mainContent;

        if (targetElement) {
            if (!targetElement.hasAttribute('tabindex')) {
                targetElement.setAttribute('tabindex', '-1');
            }

            targetElement.focus({ preventScroll: true });
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    const cantGetSettingsBecauseNotOnline = lastSettings && !allSettings && errorAllSettings && !isOnline;

    // not online
    // and also never loaded the page, so doesn't have settings in localstorage
    if (cantGetSettingsBecauseNotOnline) {
        console.log('DEBUG0 SiteSetup will return NoInternetPage', {lastSettings, allSettings, errorAllSettings, isOnline});
    }

    return (
        <>
            {cantGetSettingsBecauseNotOnline ? (
                <NoInternetPage />
            ) : (
                <>
                    <SplashScreen visible={!allSettings} />
                    <a className="skip-to-content" href="#main-content" onClick={handleSkipToContent}>
                        {translations.skipToContent}
                    </a>
                    <GlobalStylesUpdater />
                    <LocationLogger />
                    <NotificationListener />
                    <CookieConsentManager />
                    <main id="main-content" tabIndex={-1}>
                        <Outlet key={i18n.language} />
                    </main>
                    {/* Container for Klaro UI to ensure it's inside our app tree */}
                    <div id="bc-cookie-manager" />
                </>
            )}
        </>
    );
};

const NotificationListener: FC = () => {
    const { navigate } = useCustomNavigate();

    useEffect(() => {
        const handleNotificationClick = (event: Event) => {
            const customEvent = event as CustomEvent<{ path: string }>;
            const path = customEvent.detail.path;
            navigate(path);
        };

        window.addEventListener('notification-click', handleNotificationClick);
        return () => {
            window.removeEventListener('notification-click', handleNotificationClick);
        };
    }, [navigate]);

    return null;
};

const LOCATION_KEY = 'location';

const LocationLogger: FC = () => {
    const { showNotification } = usePushNotification();
    const { login, user, allSettings, storesLocations, lastUserLocationLogged, setLastUserLocationLogged } = useData();
    const { request: requestRegisterUserLocations } = useRegisterUserLocations();
    const { request: requestStoresLocations } = useGetStoresLocations();

    const { t } = useTranslation();

    const translations = {
        welcomeToMarketplaceMessage: t('notifications.welcome-to-marketplace-message', {
            name: allSettings?.data?.marketplaceName,
            interpolation: { escapeValue: false },
        }),
        closeToMerchantDescription: (name: string) =>
            t('notifications.close-to-merchant-description', { name: name, interpolation: { escapeValue: false } }),
    };

    // update translations according to services
    useEffect(() => {
        translations.welcomeToMarketplaceMessage = t('welcome-to-marketplace-message', {
            name: allSettings?.data?.marketplaceName,
            interpolation: { escapeValue: false },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSettings?.data?.marketplaceName]);

    // request store locations if notify_merchant_proximity is active
    useEffect(() => {
        if (
            allSettings?.data?.module_beacons &&
            allSettings.data.module_beacons.active &&
            allSettings.data.module_beacons.notify_merchant_proximity &&
            storesLocations === null
        ) {
            requestStoresLocations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSettings?.data?.module_beacons?.active, allSettings?.data?.module_beacons?.notify_merchant_proximity]);

    const getAllStoresCoordinates = useCallback(() => {
        return Object.entries(storesLocations?.data ?? {}).flatMap(([city, locations]) => {
            if (!Array.isArray(locations)) {
                console.warn(`Invalid data for city ${city}:`, locations);
                return [];
            }

            return locations.map((location) => ({
                uuid: location.uuid ?? '',
                name: location.name ?? '',
                latitude: location.latitude ?? 0,
                longitude: location.longitude ?? 0,
            }));
        });
    }, [storesLocations]);

    const locationIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const lastLocationLoggedRef = useRef<UserLocation | null>(null);
    const lastStoreProximityNotificationRef = useRef<{
        uuid: string;
        name: string;
        latitude?: number;
        longitude?: number;
        createdAt: number;
    } | null>(null);

    const userAllowsGeolocationTracking = login ? user?.data?.allowGeolocationTracking : true;

    // in minutes
    const interval: number | undefined = allSettings?.data?.module_beacons?.update_interval_in_minutes;
    const storeProximityInterval: number | undefined = allSettings?.data?.module_beacons?.notification_interval_in_minutes;

    const coordinates: string[][] = allSettings?.data?.marketplaceCoordinates || [];

    const isMobileDevice: () => boolean = () => {
        // cspell:disable-next-line
        return /Mobi|Android|iPhone|iPad|iPod|Opera Mini|Windows Phone|BlackBerry|BB10|Silk|webOS|Mobile|UCWEB|UCBrowser|SamsungBrowser/i.test(
            navigator.userAgent
        );
    };

    // track user's location only if
    // - beacons module is active
    // - an update interval is set
    // - user did not revoke permission to track his location
    const doLog = isMobileDevice() && allSettings?.data?.module_beacons?.active && userAllowsGeolocationTracking ? true : false;

    // insert new item, only if the new item is the most recent of the list
    // this is to prevent too many logs being stored in localstorage, when multiple browser tabs are open
    function insertSortedIfLatest<T extends { created_at: string }>(array: T[], newItem: T): T[] {
        if (array.length === 0) return [newItem];

        const lastItem = array[array.length - 1];
        const lastItemTime = new Date(lastItem.created_at).getTime();
        const newItemTime = new Date(newItem.created_at).getTime();

        if (newItemTime > lastItemTime) {
            return [...array, newItem];
        }

        return array;
    }

    const getCurrentPosition = (position: GeolocationPosition, now: number) => {
        const { latitude, longitude } = position.coords;

        const createdAtTime = new Date(now).getTime();

        // format as "YYYY-MM-DD HH:MM:SS" in UTC
        const formattedCreatedAtTime = new Date(createdAtTime).toISOString().replace('T', ' ').split('.')[0];

        let type: 'in' | 'out' = 'out';

        // ensure we have valid polygon data
        if (coordinates.length >= 3) {
            const area = L.polygon(coordinates.map(([lat, lng]) => [parseFloat(lat), parseFloat(lng)]));

            // check if the user is inside the area
            if (area.getBounds().contains([latitude, longitude])) {
                type = 'in';
            }
        }

        return { latitude, longitude, type, created_at: formattedCreatedAtTime };
    };

    // when user logs in, register records on db, and remove from localstorage
    useEffect(() => {
        if (login) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const existingData: any[] = loadFromLocalStorage(LOCATION_KEY) || [];

            if (!existingData || existingData.length === 0) {
                return;
            }

            requestRegisterUserLocations({ locations: existingData });
            saveToLocalStorage(LOCATION_KEY, []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    // always know the time of the last record, to prevent too many records being recorded from multiple tabs
    useEffect(() => {
        // if beacons module is active and notify_enter_geographic_area is true
        // notify when user enters the marketplace
        if (
            lastLocationLoggedRef.current &&
            lastLocationLoggedRef.current.type === 'out' &&
            lastUserLocationLogged?.type === 'in' &&
            allSettings?.data?.module_beacons?.notify_enter_geographic_area === true
        ) {
            logger.log('pages > LocationLogger > user will be notified about entering marketplace area');
            showNotification(allSettings?.data?.marketplaceName || '', translations.welcomeToMarketplaceMessage);
        }

        // if beacons module is active and notify_merchant_proximity and is true
        // notify when user comes in proximity of store with an interval defined in config notification_interval_in_minutes
        if (
            allSettings?.data?.module_beacons?.notify_merchant_proximity &&
            allSettings?.data?.module_beacons?.notification_interval_in_minutes
        ) {
            const now = new Date().getTime();
            const lastStoreProximityNotificationTime = lastStoreProximityNotificationRef.current?.createdAt || 0;
            const storeProximityIntervalMs = storeProximityInterval ? minutesToMilliseconds(storeProximityInterval) : null;
            const elapsedTime = getElapsedTime(lastStoreProximityNotificationTime, now);

            // prevent notification if interval time hasn't elapsed
            if (!storeProximityIntervalMs || elapsedTime < storeProximityIntervalMs) {
                return;
            }

            const closestStore =
                lastLocationLoggedRef.current?.latitude && lastLocationLoggedRef.current?.longitude
                    ? getClosestStore(
                          lastLocationLoggedRef.current.latitude,
                          lastLocationLoggedRef.current.longitude,
                          getAllStoresCoordinates(),
                          lastStoreProximityNotificationRef.current?.uuid ? [lastStoreProximityNotificationRef.current?.uuid] : []
                      )
                    : null;

            if (closestStore) {
                showNotification(
                    allSettings?.data?.marketplaceName || '',
                    translations.closeToMerchantDescription(closestStore.name || ''),
                    (): void => {
                        window.focus();
                        window.dispatchEvent(
                            new CustomEvent('notification-click', {
                                detail: { path: SITE_URLS.merchantDetail.replace(':uuid', `${closestStore.uuid}`) },
                            })
                        );
                    }
                );
            }

            lastStoreProximityNotificationRef.current = {
                uuid: closestStore?.uuid || '',
                name: closestStore?.name || '',
                latitude: closestStore?.latitude,
                longitude: closestStore?.longitude,
                createdAt: now,
            };
        }

        lastLocationLoggedRef.current = lastUserLocationLogged || null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastUserLocationLogged?.createdAt]);

    useEffect(() => {
        const sessionId = getSessionId();

        if (!navigator.geolocation) {
            console.error('Geolocation is not supported by this browser.');
            return;
        }

        // get and save location
        const logLocation = () => {
            logger.log('pages > LocationLogger > logLocation');

            const now = new Date().getTime();
            const lastLoggedTime = lastLocationLoggedRef.current?.createdAt || 0;
            const intervalMs = interval ? minutesToMilliseconds(interval) : null;
            const elapsedTime = getElapsedTime(lastLoggedTime, now);

            // prevent logging if interval time hasn't elapsed
            if (!intervalMs || elapsedTime < intervalMs) {
                logger.log('pages > LocationLogger > location wont be logged because not enough time has elapsed');
                return;
            }

            logger.log('pages > LocationLogger > location will be logged because enough time has elapsed');

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    logger.log('pages > LocationLogger > navigator.geolocation.getCurrentPosition');

                    const { latitude, longitude, type, created_at } = getCurrentPosition(position, now);

                    // if user is logged in, save to db via API
                    if (login) {
                        requestRegisterUserLocations({
                            locations: [{ latitude, longitude, type, created_at, session_id: sessionId }],
                        });
                    }
                    // if user is not logged in, save to localstorage
                    else {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const existingData: any[] = loadFromLocalStorage(LOCATION_KEY) || [];

                        // add the new entry
                        const updatedData = insertSortedIfLatest(existingData, {
                            latitude,
                            longitude,
                            type,
                            created_at,
                            session_id: sessionId,
                        });
                        saveToLocalStorage(LOCATION_KEY, updatedData);
                        setLastUserLocationLogged({ latitude, longitude, type, createdAt: now });
                    }
                },
                (error) => {
                    logger.log('pages > LocationLogger > error getting location', { error });
                }
            );
        };

        // log location only on mobile devices
        if (interval && doLog) {
            logger.log("pages > LocationLogger > user's locations will be logged");

            const intervalMs = minutesToMilliseconds(interval);

            logLocation();
            locationIntervalRef.current = setInterval(() => {
                logLocation();
            }, intervalMs);
        } else {
            logger.log("pages > LocationLogger > user's locations won't be logged");
        }

        // clear interval
        return () => {
            if (locationIntervalRef.current) {
                logger.log('pages > LocationLogger > update, clearing log interval');
                clearInterval(locationIntervalRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSettings?.data, user?.data?.allowGeolocationTracking]);

    return null;
};
